import React, { useRef, useState } from "react";
import { focusPreviewSection } from "./previewScreen";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  type MobileScreenSettingsScreenTypes as MobileScreenSettingsScreenType,
  MobileScreenSettingsScreenTypes,
  MobileScreenSettingsWithRelations,
  UpdateMobileScreenSettings,
} from "@/types/mobile-settings";
import { useMobileSettings } from "./hooks/useMobileSettings";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useHotelSubscription } from "@/components/pages/hotels/hooks/useHotelSubscriptions";
import { DashboardSettings } from "./components/DashboardSettings";
import { PhonePreview } from "@/components/common/PhonePreview";
import { GuestAppPreview } from "./components/GuestAppPreview";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SettingsNavigation } from "./components/Navigation";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { HotelServiceProviderType } from "@/types/hotelService";
import {
  buildCapabilities,
  type CapabilityKey,
  type CapabilityMap,
  type CanonicalBooleanFeatureKey,
  type CorePlatformFeatureKey,
  hasCorePlatformAccess,
  hasBooleanFeature,
  hasHotelSubscriptionFeature,
  isHotelScopedSubscriptionRole,
  isRapidPlan,
  isStarterPlan,
  isTenantAdminRole,
  toFeatureMap,
} from "@/utils/accessControl";
import { useSelectedHotelId } from "@/utils/hotelContext";
import type { HotelSubscriptionFeature } from "@/types/hotel-subscription";

interface DashboardScreenConfig {
  featureKey: CorePlatformFeatureKey;
  subscriptionFeatureKey?: CanonicalBooleanFeatureKey;
  userCapabilityKey?: CapabilityKey;
  value: MobileScreenSettingsScreenType;
  labelKey: string;
  defaultLabel: string;
}

const DASHBOARD_SCREEN_CONFIG = [
  {
    featureKey: "arrivals",
    subscriptionFeatureKey: "checkin_checkout",
    userCapabilityKey: "canUseCheckinCheckout",
    value: MobileScreenSettingsScreenTypes.BEFORE_CHECKIN,
    labelKey: "mobileSettings.dashboard.screenTabs.beforeCheckIn",
    defaultLabel: "Before check-in",
  },
  {
    featureKey: "in_house",
    value: MobileScreenSettingsScreenTypes.DURING_STAY,
    labelKey: "mobileSettings.dashboard.screenTabs.inHouse",
    defaultLabel: "In house",
  },
  {
    featureKey: "departures",
    subscriptionFeatureKey: "checkin_checkout",
    userCapabilityKey: "canUseCheckinCheckout",
    value: MobileScreenSettingsScreenTypes.AFTER_CHECKOUT,
    labelKey: "mobileSettings.dashboard.screenTabs.beforeCheckOut",
    defaultLabel: "Before check-out",
  },
] as const satisfies readonly DashboardScreenConfig[];

const isSubscriptionFeatureEnabled = (
  features: HotelSubscriptionFeature[] | null | undefined,
  featureKey: string,
): boolean => {
  const feature = features?.find((item) => item.key === featureKey);

  if (!feature?.enabled) {
    return false;
  }

  if (typeof feature.value === "boolean") {
    return feature.value;
  }

  if (typeof feature.value === "string") {
    const normalizedValue = feature.value.trim().toLowerCase();

    if (normalizedValue === "false") {
      return false;
    }

    if (normalizedValue === "true" || normalizedValue.length === 0) {
      return true;
    }
  }

  if (typeof feature.value === "number") {
    return feature.value > 0;
  }

  return true;
};

const isDashboardScreenEnabledForSubscription = (
  features: HotelSubscriptionFeature[] | null | undefined,
  screen: DashboardScreenConfig,
): boolean => {
  if (screen.subscriptionFeatureKey !== undefined) {
    return hasHotelSubscriptionFeature(features, screen.subscriptionFeatureKey);
  }

  return isSubscriptionFeatureEnabled(features, screen.featureKey);
};

const isDashboardScreenEnabledForUser = (
  userProfile: ReturnType<typeof useAuthenticatedUser>["userProfile"],
  capabilities: CapabilityMap,
  screen: DashboardScreenConfig,
): boolean => {
  if (screen.userCapabilityKey !== undefined) {
    return capabilities[screen.userCapabilityKey];
  }

  return hasCorePlatformAccess(userProfile, screen.featureKey);
};

const getAvailableDashboardScreens = (
  userProfile: ReturnType<typeof useAuthenticatedUser>["userProfile"],
  hotelSubscriptionFeatures: HotelSubscriptionFeature[] | null | undefined,
) => {
  const usesHotelSubscriptionFeatures = isTenantAdminRole(userProfile);
  const capabilities = buildCapabilities(userProfile);
  const rapidPlan = isRapidPlan(userProfile);

  return DASHBOARD_SCREEN_CONFIG.filter((screen) => {
    if (
      rapidPlan &&
      screen.value !== MobileScreenSettingsScreenTypes.DURING_STAY
    ) {
      return false;
    }

    if (usesHotelSubscriptionFeatures) {
      return isDashboardScreenEnabledForSubscription(
        hotelSubscriptionFeatures,
        screen,
      );
    }

    return isDashboardScreenEnabledForUser(userProfile, capabilities, screen);
  });
};

const getAllowedServiceProviderTypes = (
  features: HotelSubscriptionFeature[] | null | undefined,
): HotelServiceProviderType[] | undefined => {
  if (!features) {
    return undefined;
  }

  const providerTypes: HotelServiceProviderType[] = [];

  if (hasHotelSubscriptionFeature(features, "hotel_services")) {
    providerTypes.push(
      HotelServiceProviderType.GUEST_SERVICE,
      HotelServiceProviderType.EXTERNAL_SERVICE,
    );
  }

  if (hasHotelSubscriptionFeature(features, "guesteria_partners")) {
    providerTypes.push(HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE);
  }

  return providerTypes;
};

const getAllowedServiceProviderTypesForUser = (
  userProfile: ReturnType<typeof useAuthenticatedUser>["userProfile"],
): HotelServiceProviderType[] | undefined => {
  if (!isHotelScopedSubscriptionRole(userProfile)) {
    return undefined;
  }

  if (!userProfile?.access?.hasActiveSubscription) {
    return [];
  }

  const featureMap = toFeatureMap(userProfile);
  const providerTypes: HotelServiceProviderType[] = [];

  if (hasBooleanFeature(featureMap, "hotel_services")) {
    providerTypes.push(
      HotelServiceProviderType.GUEST_SERVICE,
      HotelServiceProviderType.EXTERNAL_SERVICE,
    );
  }

  if (hasBooleanFeature(featureMap, "guesteria_partners")) {
    providerTypes.push(HotelServiceProviderType.GUESTERIA_PARTNERS_SERVICE);
  }

  return providerTypes;
};

const isServiceAllowedByProviderTypes = (
  serviceProviderTypes: HotelServiceProviderType[] | undefined,
  providerType: string | undefined,
): boolean => {
  if (!serviceProviderTypes) {
    return true;
  }

  return serviceProviderTypes.includes(
    providerType as HotelServiceProviderType,
  );
};

const filterSettingsServicesByProviderTypes = (
  settings: MobileScreenSettingsWithRelations | null | undefined,
  serviceProviderTypes: HotelServiceProviderType[] | undefined,
): MobileScreenSettingsWithRelations | null | undefined => {
  if (!settings || !serviceProviderTypes) {
    return settings;
  }

  const isAllowed = (providerType: string | undefined) =>
    isServiceAllowedByProviderTypes(serviceProviderTypes, providerType);

  return {
    ...settings,
    mobileScreenSettingsRecommendedServices:
      settings.mobileScreenSettingsRecommendedServices.filter((item) =>
        isAllowed(item.service.providerType),
      ),
    mobileScreenSettingsFeaturedActivities:
      settings.mobileScreenSettingsFeaturedActivities.filter((item) =>
        isAllowed(item.service.providerType),
      ),
    mobileScreenSettingsCtaServices:
      settings.mobileScreenSettingsCtaServices.filter((item) =>
        isAllowed(item.service.providerType),
      ),
  };
};

export const DashboardSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { userProfile, isLoading: isUserAccessLoading } =
    useAuthenticatedUser();
  const selectedHotelId = useSelectedHotelId();
  const hotelId = selectedHotelId ?? userProfile?.profile?.hotelId ?? "";
  const shouldUseHotelSubscriptionFeatures = isTenantAdminRole(userProfile);
  const { data: hotelSubscription, isLoading: isHotelSubscriptionLoading } =
    useHotelSubscription(
      hotelId || null,
      shouldUseHotelSubscriptionFeatures && Boolean(hotelId),
    );
  const serviceProviderTypes = React.useMemo(
    () =>
      shouldUseHotelSubscriptionFeatures
        ? getAllowedServiceProviderTypes(hotelSubscription?.features)
        : getAllowedServiceProviderTypesForUser(userProfile),
    [
      hotelSubscription?.features,
      shouldUseHotelSubscriptionFeatures,
      userProfile,
    ],
  );
  const hasHotelServicesAccess =
    serviceProviderTypes === undefined ||
    serviceProviderTypes.includes(HotelServiceProviderType.GUEST_SERVICE) ||
    serviceProviderTypes.includes(HotelServiceProviderType.EXTERNAL_SERVICE);
  const availableScreens = React.useMemo(
    () =>
      getAvailableDashboardScreens(userProfile, hotelSubscription?.features),
    [hotelSubscription?.features, userProfile],
  );
  const rapidPlan = isRapidPlan(userProfile);
  const hideServiceSections = rapidPlan || isStarterPlan(userProfile);
  const previewScreens = rapidPlan ? availableScreens : DASHBOARD_SCREEN_CONFIG;
  const defaultScreenType =
    availableScreens.find(
      (screen) => screen.value === MobileScreenSettingsScreenTypes.DURING_STAY,
    )?.value ??
    availableScreens[0]?.value ??
    MobileScreenSettingsScreenTypes.DURING_STAY;
  const previewSectionRef = useRef<HTMLDivElement>(null);
  const handlePreviewScreen = () =>
    focusPreviewSection(previewSectionRef.current);
  const [screenType, setScreenType] =
    useState<MobileScreenSettingsScreenType>(defaultScreenType);

  const { settings, isLoading, updateSettings } = useMobileSettings(
    hotelId,
    screenType,
  );

  const [localSettings, setLocalSettings] =
    useState<MobileScreenSettingsWithRelations | null>(null);
  const settingsForSubscription = React.useMemo(
    () =>
      filterSettingsServicesByProviderTypes(
        localSettings || settings,
        serviceProviderTypes,
      ),
    [localSettings, serviceProviderTypes, settings],
  );

  React.useEffect(() => {
    setLocalSettings(settings ?? null);
  }, [settings]);

  React.useEffect(() => {
    const screenIsAvailable = availableScreens.some(
      (screen) => screen.value === screenType,
    );

    if (!screenIsAvailable) {
      setScreenType(defaultScreenType);
    }
  }, [availableScreens, defaultScreenType, screenType]);

  const handleLocalUpdate = (
    updatedData: Partial<MobileScreenSettingsWithRelations>,
  ) => {
    if (!localSettings) return;
    setLocalSettings({ ...localSettings, ...updatedData });
  };

  const { toast } = useToast();

  const handleSave = async () => {
    if (!settingsForSubscription) return;

    const updateData: UpdateMobileScreenSettings = {
      hotelId: settingsForSubscription.hotelId,
      screenType: settingsForSubscription.screenType,
      isAddressVisible: settingsForSubscription.isAddressVisible,
      shortcuts: settingsForSubscription.mobileScreenSettingsShortcuts.map(
        (s) => ({ name: s.name, iconId: s.iconId ?? undefined }),
      ),
      faqIds:
        settingsForSubscription.mobileScreenSettingsFaqs?.map(
          (faq) => faq.faqId,
        ) ?? [],
      recommendedServiceIds:
        settingsForSubscription.mobileScreenSettingsRecommendedServices.map(
          (s) => s.serviceId,
        ),
      featuredServiceIds:
        settingsForSubscription.mobileScreenSettingsFeaturedActivities.map(
          (s) => s.serviceId,
        ),
      ctaServiceIds:
        settingsForSubscription.mobileScreenSettingsCtaServices.map(
          (s) => s.serviceId,
        ),
      pageIds: settingsForSubscription.mobileScreenSettingsPages.map(
        (page) => page.customPageId,
      ),
    };

    await updateSettings(updateData);
    toast({
      title: t("common.success"),
      description: t("mobileSettings.dashboard.updateSuccess", {
        defaultValue: "Dashboard settings updated successfully",
      }),
    });
  };

  if (isLoading || isUserAccessLoading) {
    return (
      <div className="p-8">
        {t("common.loading", { defaultValue: "Loading..." })}
      </div>
    );
  }
  if (shouldUseHotelSubscriptionFeatures && isHotelSubscriptionLoading) {
    return (
      <div className="p-8">
        {t("common.loading", { defaultValue: "Loading..." })}
      </div>
    );
  }
  if (!settings) {
    return (
      <div className="p-8">
        {t("mobileSettings.dashboard.notFound", {
          defaultValue: "No settings found.",
        })}
      </div>
    );
  }

  return (
    <div className="font-patrick flex h-full overflow-hidden bg-white">
      <div className="hide-scrollbar flex-3 overflow-y-auto border-r border-zinc-200 p-12">
        <SettingsNavigation activeTab="dashboard" />

        <DashboardSettings
          settings={settingsForSubscription || settings}
          onUpdate={handleLocalUpdate}
          onSave={handleSave}
          screenType={screenType}
          onScreenTypeChange={setScreenType}
          serviceProviderTypes={serviceProviderTypes}
          showServiceShortcut={hasHotelServicesAccess}
          hideServiceSections={hideServiceSections}
        />
      </div>

      <div className="hide-scrollbar flex flex-2 flex-col items-center gap-8 overflow-y-auto bg-zinc-50/50 p-8">
        <div className="flex w-full items-center gap-4">
          <Select defaultValue="original">
            <SelectTrigger className="flex-1 rounded-xl border-2 border-zinc-900 bg-white text-lg font-bold">
              <SelectValue
                placeholder={t("mobileSettings.preview.selectVersion", {
                  defaultValue: "Select version",
                })}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="original">
                {t("mobileSettings.preview.original", {
                  defaultValue: "Original",
                })}
              </SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            onClick={handlePreviewScreen}
            className="h-12 flex-1 rounded-xl border-2 border-zinc-900 bg-white text-lg font-bold hover:bg-zinc-50"
          >
            {t("mobileSettings.preview.button", {
              defaultValue: "Preview screen",
            })}
          </Button>
        </div>

        <div
          ref={previewSectionRef}
          tabIndex={-1}
          className="flex w-full flex-col items-center gap-4 outline-none"
        >
          <h2 className="text-2xl font-bold">
            {t("mobileSettings.preview.title", {
              defaultValue: "Guest App Preview",
            })}
          </h2>

          <Tabs
            value={screenType}
            onValueChange={(value) =>
              setScreenType(value as MobileScreenSettingsScreenTypes)
            }
            className="w-full max-w-[400px]"
          >
            <TabsList className="h-auto w-full justify-between bg-transparent p-0">
              {previewScreens.map((screen) => {
                const available = availableScreens.some(
                  (candidate) => candidate.value === screen.value,
                );

                return (
                  <TabsTrigger
                    key={screen.value}
                    value={screen.value}
                    disabled={!available}
                    className={cn(
                      "rounded-lg bg-transparent px-4 py-2 text-zinc-500 data-[state=active]:bg-zinc-200 data-[state=active]:text-zinc-900",
                      !available &&
                        "cursor-not-allowed opacity-40 hover:bg-transparent",
                    )}
                  >
                    {t(screen.labelKey, {
                      defaultValue: screen.defaultLabel,
                    })}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          <PhonePreview
            serviceHeader={false}
            containerClassName="w-[400px] rounded-[3rem] border-[12px] border-zinc-900 shadow-2xl"
          >
            <GuestAppPreview
              settings={settingsForSubscription || settings}
              isRapidPlan={rapidPlan}
            />
          </PhonePreview>
        </div>
      </div>
    </div>
  );
};
